/**
 * 68.3: the squad strip's data at L0 (DIRECTION §0.6 "the squad strip", §0.7 identities) — a card a squad of your team,
 * in the table's own words (core/commandTable.ts: its label, its order and the order's glyph, its members, its status),
 * numbered for the keys: **1 is the squad of the unit you hold**, the rest after it in the table's order; 0 is all of
 * them (no squad cap, the owner: the keys reach the first four, the strip shows every one).
 *
 * **The knowledge limit.** On the ground you do not see your other squads: a card shows the squad's **last report** —
 * its members' health and its status as they were when you last saw one of them — and how old that report is, never
 * the truth. Your own squad, and a squad any of whose living members you see now, is **fresh** (the report is now);
 * one seen within REPORT_STALE_SECONDS is an **old report** (stale); beyond that, or never seen, it is **out of sight**.
 * The squad's order is yours to know (you gave it, or the commander did), so it is always current. Pure: the host
 * passes what the viewer sees (`sees`) and keeps the reports between frames.
 */
import type { GameState, PlayerState } from '../types';
import { commandTable, type FriendlyStatus, type SquadMarker } from './commandTable';
import { squadOf, squadOrderFor } from './squads';

/** Past this many seconds an old report counts as out of sight. */
export const REPORT_STALE_SECONDS = 30;

/** What the viewer last knew of a squad: its members' health and its status, and when (an action tick). */
export interface SquadReport { tick: number; members: Array<{ id: number; alive: boolean; health: number }>; status: FriendlyStatus }

export type CardFreshness = 'fresh' | 'stale' | 'outOfSight';

export interface SquadCard {
  id: number;
  /** 1 for the squad of the unit you hold, then 2, 3, … in the table's order. */
  number: number;
  label: string;
  own: boolean;
  /** The order in words and its glyph (the table's), always current. */
  order: string;
  glyph: string;
  orderKind: string | null;
  /** The members as last reported (a pip each); empty before any report. */
  members: Array<{ alive: boolean; health: number }>;
  status: FriendlyStatus | null;
  freshness: CardFreshness;
  /** Seconds since the report (0 when fresh; null never reported). */
  age: number | null;
  /** No member alive (as reported). */
  spent: boolean;
  /** 68.3b: the member who leads it now (its first living one; null when none stands) — the strip marks them. */
  leader: number | null;
}

/**
 * The cards for team `team` as `viewer` knows them at `tick`, and the reports to keep for the next call. `sees(unit)`
 * says whether the viewer sees that teammate now (the host's line of sight; the viewer null at L1 = the commander sees
 * everyone, and every card is fresh). `dt` is the tick length.
 */
export function squadCards(state: GameState, team: number, viewer: PlayerState | null, tick: number, reports: ReadonlyMap<number, SquadReport>, sees: (unit: PlayerState) => boolean, dt = 1 / 120): { cards: SquadCard[]; reports: Map<number, SquadReport> } {
  const table = commandTable(state, team, tick, undefined, dt);
  const ownId = viewer ? squadOf(state, viewer.id)?.id ?? null : null;
  const ordered = [...table.squads].sort((a, b) => (a.id === ownId ? -1 : b.id === ownId ? 1 : 0));
  const next = new Map(reports);
  const cards = ordered.map((m: SquadMarker, k): SquadCard => {
    const living = m.members.filter(x => x.alive).map(x => state.players.find(p => p.id === x.id)!).filter(Boolean);
    const fresh = viewer === null || m.id === ownId || living.some(p => p.id === viewer.id || sees(p));
    if (fresh) next.set(m.id, { tick, members: m.members.map(x => ({ id: x.id, alive: x.alive, health: x.health })), status: m.status });
    const report = next.get(m.id) ?? null;
    const age = report ? (tick - report.tick) * dt : null;
    const freshness: CardFreshness = fresh ? 'fresh' : age !== null && age <= REPORT_STALE_SECONDS ? 'stale' : 'outOfSight';
    const members = report ? report.members.map(x => ({ alive: x.alive, health: x.health })) : [];
    return {
      id: m.id, number: k + 1, label: m.label, own: m.id === ownId,
      order: m.order, glyph: m.glyph, orderKind: orderKindOf(state, m, tick),
      members, status: report?.status ?? null, freshness, age: fresh ? 0 : age,
      spent: report ? report.members.every(x => !x.alive) : false,
      leader: m.members.find(x => x.alive)?.id ?? null,
    };
  });
  return { cards, reports: next };
}

/** The order's kind, read as the table reads the order (its first living member's). */
function orderKindOf(state: GameState, m: SquadMarker, tick: number): string | null {
  const lead = m.members.find(x => x.alive);
  return lead && state.squads?.some(s => s.id === m.id) ? squadOrderFor(state, lead.id, tick)?.kind ?? null : null;
}

/** The squads a number key selects: 1–9 that card's squad (none past the last), 0 every squad of the team. */
export function squadsForKey(cards: readonly SquadCard[], n: number): number[] {
  if (n === 0) return cards.map(c => c.id);
  const c = cards.find(x => x.number === n);
  return c ? [c.id] : [];
}

/**
 * 68.3b: who leads each squad — its first living member (the table's lead, core/commandTable.ts; 68.10's seniority when
 * fluid squads land), every team's: the leader wears lane E's leader kit (68.8), so one reads from afar, friend or foe.
 */
export function squadLeaders(state: Pick<GameState, 'squads' | 'players'>): Set<number> {
  const out = new Set<number>();
  for (const sq of state.squads ?? []) {
    const lead = sq.unitIds.find(id => state.players.some(p => p.id === id && p.alive));
    if (lead !== undefined) out.add(lead);
  }
  return out;
}
